import { Component, EventEmitter, HostBinding, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { Subject, concatMap, filter, of, take, takeUntil } from "rxjs";
import { LoggingService } from "src/app/core";
import { ScrollerHorizontalComponent, SearchFilterValue, SearchFiltersService } from "src/app/shared";
import { VisionboardAttributesService, VisionboardCategoriesService } from "../../../services";

const Workflow: string = "VisionboardItemFilter";
const WorkflowSource: string = "VisionboardItemFilterScroller";

@Component({
    selector: "app-visionboard-item-filter-scroller",
    templateUrl: "./visionboard-item-filter-scroller.component.html",
    styleUrls: ["./visionboard-item-filter-scroller.component.scss"],
    standalone: false
})
export class VisionboardItemFilterScrollerComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();
  private _primaryFilterAttributeName: string;
  private _filterSourceValues: SearchFilterValue[];
  private _isSelfFilterChange: boolean;

  filterValues: SearchFilterValue[];
  loaded: boolean;

  @ViewChild(ScrollerHorizontalComponent) scrollerHorizontal: ScrollerHorizontalComponent;
  @HostBinding("style.display") display: string = "none";

  @Output() onLoaded: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private _visionboardCategoriesService: VisionboardCategoriesService,
    private _visionboardAttributesService: VisionboardAttributesService,
    private _searchFiltersService: SearchFiltersService,
    private _loggingService: LoggingService,
  ) {}

  ngOnInit(): void {
    this._visionboardCategoriesService.selectedCategory$
      .pipe(
        filter(selectedCategory => !!selectedCategory),
        concatMap(selectedCategory => {
          if (!selectedCategory[1]?.primaryFilterAttributeName) {
            this.loaded = false;
            this.filterValues = null;
            this._filterSourceValues = null;
            this.display = "none";
            return of(null);
          }

          this._primaryFilterAttributeName = selectedCategory[1].primaryFilterAttributeName;

          return this._visionboardAttributesService.currentFilters$.pipe(
            filter(filters => !!filters),
            take(1),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe({
        next: currentFilters => {
          if (!currentFilters) {
            return;
          }

          const source = currentFilters.find(cf => cf.name === this._primaryFilterAttributeName);

          if (!source) {
            return;
          }

          this._filterSourceValues = source.values;
          this.filterValues = [
            ...this._filterSourceValues.map(fsv => {
              const newSearchFilterValue = new SearchFilterValue(fsv.value);
              newSearchFilterValue.selected = fsv.selected;

              return newSearchFilterValue;
            }),
          ];

          this.loaded = true;
          this.display = "flex";
          this.onLoaded.emit();
        },
      });

    this._searchFiltersService.filtersChanged$
      .pipe(
        filter(searchFilters => !!searchFilters && this.loaded),
        takeUntil(this._destroyed$),
      )
      .subscribe({
        next: searchFilters => {
          const source = searchFilters.find(cf => cf.name === this._primaryFilterAttributeName);

          if (!source) {
            return;
          }

          source.values.forEach(nfv => {
            this._filterSourceValues.find(_fsv => _fsv.value === nfv.value).selected = nfv.selected;
            this.filterValues.find(fv => fv.value == nfv.value).selected = nfv.selected;
          });

          if (!this._isSelfFilterChange) {
            this.scrollerHorizontal?.resetScrollLeft();
          } else {
            this._isSelfFilterChange = false;
          }
        },
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  changeFilter(filterValue: SearchFilterValue): void {
    const filterSourceValue = this._filterSourceValues.find(fsv => fsv.value === filterValue.value);
    filterSourceValue.selected = !filterSourceValue.selected;
    this._isSelfFilterChange = true;
    this._searchFiltersService.raiseFiltersChanged();

    this._loggingService.logWorkflow(
      "VB item scroller filter change",
      "VisionboardItemFilterScrollerComponent.changeFilter",
      Workflow,
      "VbItemFilterScrollerChange",
      WorkflowSource,
      {
        FilterValue: filterValue.value,
        FilterSelected: `${filterValue.selected}`,
        FilterAttributeName: this._primaryFilterAttributeName,
        FilterCategory: this._visionboardCategoriesService.getSelectedCategory()[0].name,
        FilterSubCategory: this._visionboardCategoriesService.getSelectedCategory()[1].name,
      },
    );
  }
}
